(() => {
  class CustomSubscriptionWidget extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.formId;
      this.forms = this.resolveForms();
      this.form = this.forms[0] || null;
      this.otpOption = this.querySelector('[data-subscription-mode="otp"]');
      this.subOption = this.querySelector('[data-subscription-mode="subscription"]');
      this.otpRadio = this.querySelector('[data-subscription-radio="otp"]');
      this.subRadio = this.querySelector('[data-subscription-radio="subscription"]');
      this.planPills = Array.from(this.querySelectorAll('[data-selling-plan-id]'));
      this.subPrice = this.querySelector('[data-subscription-price]');
      this.saveBadge = this.querySelector('[data-subscription-save-badge]');

      if (!this.otpOption || !this.subOption || !this.planPills.length) return;

      this.ensureSellingPlanInputs();
      this.bindEvents();
      this.applyMode(this.dataset.initialMode === 'subscription' ? 'subscription' : 'otp');
    }

    resolveForms() {
      if (!this.formId) return [];
      
      return Array.from(document.querySelectorAll(`form[id="${CSS.escape(this.formId)}"]`));
    }

    ensureSellingPlanInputs() {
      this.sellingPlanInputs = this.forms.map((form) => {
        let input = form.querySelector('input[name="selling_plan"]');
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'selling_plan';
          form.appendChild(input);
        }
        return input;
      });

      this.sellingPlanInput = this.sellingPlanInputs[0] || null;
    }

    bindEvents() {
      this.otpOption.addEventListener('click', () => this.applyMode('otp'));
      this.subOption.addEventListener('click', () => this.applyMode('subscription'));
      this.otpOption.addEventListener('keydown', (event) => this.onModeKeydown(event, 'otp'));
      this.subOption.addEventListener('keydown', (event) => this.onModeKeydown(event, 'subscription'));

      this.planPills.forEach((pill) => {
        pill.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.setActivePlan(pill);
          this.applyMode('subscription');
        });
      });
    }

    onModeKeydown(event, mode) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this.applyMode(mode);
    }

    setActivePlan(targetPill) {
      this.planPills.forEach((pill) => {
        const isActive = pill === targetPill;
        pill.classList.toggle('is-active', isActive);
        pill.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
      this.syncPlanMeta(targetPill);
    }

    getActivePlan() {
      return this.planPills.find((pill) => pill.classList.contains('is-active')) || this.planPills[0];
    }

    syncPlanMeta(pill) {
      if (!pill) return;

      if (this.subPrice && pill.dataset.sellingPlanPrice) {
        this.subPrice.textContent = pill.dataset.sellingPlanPrice;
      }

      if (this.saveBadge) {
        const save = Number(pill.dataset.sellingPlanSave || 0);
        if (save > 0) {
          this.saveBadge.textContent = `SAVE ${save}%`;
          this.saveBadge.hidden = false;
        } else {
          this.saveBadge.hidden = true;
        }
      }

      this.syncBenefits(pill);
    }

    syncBenefits(pill) {
      const benefitsEl = this.querySelector('[data-subscription-benefits]');
      const defaultTpl = this.querySelector('template[data-benefits-default]');
      const belowTpl = this.querySelector('template[data-benefits-below]');
      if (!benefitsEl || !defaultTpl) return;

      const threshold = Number(this.dataset.benefitsPriceThreshold || 4860);
      const priceCents = Number(pill?.dataset.sellingPlanPriceCents || 0);
      const belowHtml = belowTpl?.innerHTML.trim() || '';
      const useBelow = Boolean(belowHtml) && priceCents < threshold;
      const source = useBelow ? belowTpl : defaultTpl;
      const nextHtml = source.innerHTML.trim();

      benefitsEl.innerHTML = nextHtml;
      benefitsEl.hidden = !nextHtml;
    }

    updateBuyButtonPrice(mode, activePlan) {
      if (!this.forms.length) return;

      const stickyRoot = document.querySelector(`product-sticky-bar[form="${this.formId}"]`);
      const stickyPrice = stickyRoot?.querySelector('[data-sticky-atc-price]');
      const stickySeparator = stickyRoot?.querySelector('[data-sticky-atc-separator]');
      const stickyMetaPrice = stickyRoot?.querySelector('[data-sticky-meta-price]');

      const otpPrice = this.otpOption?.dataset.otpPrice || '';
      const subPrice = activePlan?.dataset.sellingPlanPrice || '';
      const nextPrice = mode === 'subscription' ? subPrice : otpPrice;

      if (!nextPrice) return;

      this.forms.forEach((form) => {
        const buttonPrice = form.querySelector('[data-buy-button-price]');
        const buttonSeparator = form.querySelector('[data-buy-button-separator]');

        if (buttonPrice && buttonSeparator) {
          buttonPrice.textContent = nextPrice;
          buttonSeparator.hidden = false;
        }
      });

      if (stickyPrice) {
        stickyPrice.textContent = nextPrice;
      }

      if (stickySeparator) {
        stickySeparator.hidden = false;
      }

      if (stickyMetaPrice) {
        stickyMetaPrice.textContent = nextPrice;
      }
    }

    applyMode(mode) {
      const isSubscription = mode === 'subscription';
      this.classList.toggle('mode-subscription', isSubscription);
      this.classList.toggle('mode-otp', !isSubscription);

      this.otpOption.classList.toggle('is-selected', !isSubscription);
      this.subOption.classList.toggle('is-selected', isSubscription);
      this.otpOption.setAttribute('aria-pressed', !isSubscription ? 'true' : 'false');
      this.subOption.setAttribute('aria-pressed', isSubscription ? 'true' : 'false');

      if (this.otpRadio) this.otpRadio.classList.toggle('custom-subscription-widget__radio--empty', isSubscription);
      if (this.subRadio) this.subRadio.classList.toggle('custom-subscription-widget__radio--empty', !isSubscription);

      const activePlan = this.getActivePlan();
      this.syncPlanMeta(activePlan);
      this.updateBuyButtonPrice(mode, activePlan);

      const sellingPlanValue = isSubscription && activePlan ? activePlan.dataset.sellingPlanId : '';
      (this.sellingPlanInputs || []).forEach((input) => {
        input.value = sellingPlanValue;
      });
    }
  }

  if (!window.customElements.get('custom-subscription-widget')) {
    window.customElements.define('custom-subscription-widget', CustomSubscriptionWidget);
  }
})();
